/**
 * Tenant Configuration - Subdomain-based Multi-tenancy
 *
 * This module detects the current subdomain and maps it to the appropriate
 * backend API URL and tenant-specific configuration.
 */

import logger from '../utils/logger';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';

export interface TenantConfig {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  apiBaseUrl: string;
  wsBaseUrl: string;
  theme?: {
    primaryColor: string;
    logoUrl?: string;
  };
}

// Default/fallback configuration
const DEFAULT_CONFIG: TenantConfig = {
  tenantId: 'system',
  tenantSlug: 'system',
  tenantName: 'KELZIX System',
  apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  wsBaseUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8000',
};

// Static tenant mappings - development fallback only
// In production, tenant configs should be fetched from API or set via environment variables
const TENANT_REGISTRY: Record<string, Partial<TenantConfig>> = process.env.NODE_ENV === 'development'
  ? {
    // Development-only tenant configs
    'pharma-corp': {
      tenantId: '3',
      tenantSlug: 'pharma-corp',
      tenantName: 'Pharma Corp',
      apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      wsBaseUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8000',
      theme: {
        primaryColor: '#1e40af',
        logoUrl: '/logos/pharma-corp.png',
      },
    },
    'abc': {
      tenantId: '2',
      tenantSlug: 'abc',
      tenantName: 'ABC',
      apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      wsBaseUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8000',
      theme: {
        primaryColor: '#059669',
      },
    },
    'test-company': {
      tenantId: '1',
      tenantSlug: 'test-company',
      tenantName: 'Test Company',
      apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      wsBaseUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8000',
    },
    'localhost': {
      tenantId: 'system',
      tenantSlug: 'system',
      tenantName: 'KELZIX Development',
      apiBaseUrl: 'http://localhost:8000',
      wsBaseUrl: 'ws://localhost:8000',
    },
  }
  : {
    // Production: only localhost fallback, real tenants fetched from API
    'localhost': {
      tenantId: 'system',
      tenantSlug: 'system',
      tenantName: 'KELZIX System',
      apiBaseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      wsBaseUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8000',
    },
  };

/**
 * Extract subdomain from the current hostname
 */
export const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  // Handle localhost (no subdomain)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }

  // Handle IP addresses (no subdomain)
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return null;
  }

  // Extract subdomain from hostname
  // e.g., "pharma-corp.kelzix.io" -> "pharma-corp"
  // e.g., "abc.app.kelzix.io" -> "abc"
  const parts = hostname.split('.');

  // If we have at least 3 parts (subdomain.domain.tld)
  if (parts.length >= 3) {
    return parts[0];
  }

  // If we have exactly 2 parts, check if it's a known subdomain pattern
  if (parts.length === 2) {
    return parts[0];
  }

  return null;
};

/**
 * Get tenant configuration based on current subdomain
 */
export const getTenantConfig = (): TenantConfig => {
  const subdomain = getSubdomain();

  if (!subdomain) {
    return DEFAULT_CONFIG;
  }

  const tenantOverrides = TENANT_REGISTRY[subdomain];

  if (!tenantOverrides) {
    // Unknown subdomain - return default but log warning
    logger.warn(`Unknown tenant subdomain: ${subdomain}. Using default configuration.`);
    return DEFAULT_CONFIG;
  }

  return {
    ...DEFAULT_CONFIG,
    ...tenantOverrides,
  };
};

/**
 * Get tenant config from URL parameter (for super admin context switching)
 */
export const getTenantFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('tenant');
};

/**
 * Build API URL for a specific tenant (used by super admin)
 */
export const buildTenantApiUrl = (tenantSlug: string): string => {
  const tenantConfig = TENANT_REGISTRY[tenantSlug];

  if (tenantConfig?.apiBaseUrl) {
    return tenantConfig.apiBaseUrl;
  }

  // Fallback: construct URL based on pattern
  if (process.env.NODE_ENV === 'production') {
    return `https://api.${tenantSlug}.kelzix.io`;
  }

  // Development: use environment variable or localhost with tenant header
  return process.env.REACT_APP_API_URL || 'http://localhost:8000';
};

/**
 * Tenant Context Storage Keys
 */
export const TENANT_STORAGE_KEYS = {
  CURRENT_TENANT: 'kelzix_current_tenant',
  SUPER_ADMIN_CONTEXT: 'kelzix_super_admin_context',
  API_BASE_URL: 'kelzix_api_base_url',
};

/**
 * Store super admin's selected tenant context
 */
export const setSuperAdminTenantContext = (tenantSlug: string): void => {
  safeSetItem(TENANT_STORAGE_KEYS.SUPER_ADMIN_CONTEXT, tenantSlug);
  safeSetItem(TENANT_STORAGE_KEYS.API_BASE_URL, buildTenantApiUrl(tenantSlug));
};

/**
 * Get super admin's current tenant context
 */
export const getSuperAdminTenantContext = (): string | null => {
  return safeGetItem(TENANT_STORAGE_KEYS.SUPER_ADMIN_CONTEXT);
};

/**
 * Clear super admin tenant context (return to system view)
 */
export const clearSuperAdminTenantContext = (): void => {
  safeRemoveItem(TENANT_STORAGE_KEYS.SUPER_ADMIN_CONTEXT);
  safeRemoveItem(TENANT_STORAGE_KEYS.API_BASE_URL);
};

/**
 * Get the effective API base URL
 * Priority: Super Admin Context > Subdomain Config > Default
 */
export const getEffectiveApiBaseUrl = (): string => {
  // Check for super admin context override
  const superAdminContext = safeGetItem(TENANT_STORAGE_KEYS.API_BASE_URL);
  if (superAdminContext) {
    return superAdminContext;
  }

  // Use subdomain-based config
  const config = getTenantConfig();
  return config.apiBaseUrl;
};

/**
 * Initialize tenant configuration on app load
 */
export const initializeTenantConfig = (): TenantConfig => {
  const config = getTenantConfig();

  // Store current tenant info
  safeSetItem(TENANT_STORAGE_KEYS.CURRENT_TENANT, JSON.stringify({
    tenantId: config.tenantId,
    tenantSlug: config.tenantSlug,
    tenantName: config.tenantName,
  }));

  if (process.env.NODE_ENV === 'development') {
    logger.log(`[Tenant Config] Initialized for: ${config.tenantName} (${config.tenantSlug})`);
    logger.log(`[Tenant Config] API Base URL: ${config.apiBaseUrl}`);
  }

  return config;
};

const tenantConfigModule = {
  getTenantConfig,
  getSubdomain,
  getEffectiveApiBaseUrl,
  initializeTenantConfig,
  setSuperAdminTenantContext,
  getSuperAdminTenantContext,
  clearSuperAdminTenantContext,
  buildTenantApiUrl,
  TENANT_STORAGE_KEYS,
};

export default tenantConfigModule;
